import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Shield, ArrowLeft, Upload, FileText, AlertTriangle, AlertCircle,
  CheckCircle, ChevronDown, ChevronRight, Scale, Lightbulb, PenTool,
  Layers, Copy, Loader2, Search, Filter, Info, X, FileUp, Clipboard,
  Target, BookOpen, Sparkles, Eye, Download, Save, Clock, ArrowLeftRight
} from 'lucide-react';

const PRIORITY_CONFIG = {
  critica: { 
    label: "Crítica", 
    color: "red", 
    bgClass: "bg-red-500/20", 
    textClass: "text-red-400", 
    borderClass: "border-red-500/30",
    icon: AlertTriangle
  },
  alta: { 
    label: "Alta", 
    color: "orange", 
    bgClass: "bg-orange-500/20", 
    textClass: "text-orange-400", 
    borderClass: "border-orange-500/30",
    icon: AlertCircle
  },
  media: { 
    label: "Media", 
    color: "yellow", 
    bgClass: "bg-yellow-500/20", 
    textClass: "text-yellow-400", 
    borderClass: "border-yellow-500/30",
    icon: Info
  },
  baja: { 
    label: "Baja", 
    color: "blue", 
    bgClass: "bg-blue-500/20", 
    textClass: "text-blue-400", 
    borderClass: "border-blue-500/30",
    icon: Lightbulb
  }
};

const CATEGORY_CONFIG = {
  clausula: { label: "Cláusulas", icon: FileText, color: "purple" },
  estructura: { label: "Estructura", icon: Layers, color: "blue" },
  redaccion: { label: "Redacción", icon: PenTool, color: "cyan" },
  fondo: { label: "Fondo Legal", icon: Scale, color: "amber" }
};

const TYPE_CONFIG = {
  agregar: { label: "Agregar", color: "green", icon: "+" },
  modificar: { label: "Modificar", color: "amber", icon: "~" },
  eliminar: { label: "Eliminar", color: "red", icon: "-" },
  advertencia: { label: "Advertencia", color: "orange", icon: "!" }
};

const CONTRACT_TYPES = [
  { id: "arriendo", label: "Arriendo" },
  { id: "compraventa", label: "Compraventa" },
  { id: "laboral", label: "Laboral" },
  { id: "servicios", label: "Prestación de Servicios" },
  { id: "general", label: "Otro / General" }
];

const USER_ROLES = {
  arriendo: [
    { id: "arrendador", label: "Arrendador (Dueño)" },
    { id: "arrendatario", label: "Arrendatario (Inquilino)" }
  ],
  compraventa: [
    { id: "vendedor", label: "Vendedor" },
    { id: "comprador", label: "Comprador" }
  ],
  laboral: [
    { id: "empleador", label: "Empleador" },
    { id: "trabajador", label: "Trabajador" }
  ],
  servicios: [
    { id: "prestador", label: "Prestador de Servicios" },
    { id: "mandante", label: "Mandante / Cliente" }
  ],
  general: [
    { id: "parte_a", label: "Primera Parte" },
    { id: "parte_b", label: "Segunda Parte" }
  ]
};

const ContractAnalysisPage = () => {
  const { api } = useAuth();
  
  // Input state
  const [inputMethod, setInputMethod] = useState('paste'); // 'paste' or 'upload'
  const [contractContent, setContractContent] = useState('');
  const [contractType, setContractType] = useState('');
  const [userRole, setUserRole] = useState('');
  const [fileName, setFileName] = useState('');
  
  // Analysis state
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  
  // Filter state
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [expandedSuggestion, setExpandedSuggestion] = useState(null);
  const [saving, setSaving] = useState(false);
  const [analysisName, setAnalysisName] = useState('');

  const handleFileUpload = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.txt')) {
      toast.error('Por favor suba un archivo de texto (.txt)');
      return;
    }

    try {
      const text = await file.text();
      setContractContent(text);
      setFileName(file.name);
      setInputMethod('paste'); // Switch to paste view to show content
      toast.success(`Archivo "${file.name}" cargado correctamente`);
    } catch (error) {
      toast.error('Error al leer el archivo');
    }
  }, []);

  const handlePaste = useCallback(async () => {
    try {
      const text = await navigator.clipboard.readText();
      setContractContent(text);
      toast.success('Texto pegado desde el portapapeles');
    } catch (error) {
      toast.error('No se pudo acceder al portapapeles');
    }
  }, []);

  const analyzeContract = async () => {
    if (!contractContent.trim()) {
      toast.error('Por favor ingrese el contenido del contrato');
      return;
    }

    if (contractContent.length < 100) {
      toast.error('El contrato es demasiado corto para analizar');
      return;
    }

    setAnalyzing(true);
    setAnalysis(null);

    try {
      const response = await api.post('/contract-analysis/analyze', {
        content: contractContent,
        contract_type: contractType || null,
        user_role: userRole || null
      });

      setAnalysis(response.data);
      toast.success('Análisis completado');
    } catch (error) {
      console.error('Analysis error:', error);
      toast.error('Error al analizar el contrato');
    } finally {
      setAnalyzing(false);
    }
  };

  const filteredSuggestions = analysis?.suggestions?.filter(s => {
    if (priorityFilter !== 'all' && s.priority !== priorityFilter) return false;
    if (categoryFilter !== 'all' && s.category !== categoryFilter) return false;
    return true;
  }) || [];

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copiado al portapapeles');
  };

  const resetAnalysis = () => {
    setAnalysis(null);
    setContractContent('');
    setContractType('');
    setUserRole('');
    setFileName('');
    setAnalysisName('');
  };

  const saveAnalysis = async () => {
    if (!analysis) {
      toast.error('No hay análisis para guardar');
      return;
    }

    setSaving(true);
    try {
      await api.post('/contract-analysis/save-full', null, {
        params: {
          content: contractContent,
          contract_type: contractType || null,
          user_role: userRole || null,
          name: analysisName || `Análisis de ${analysis.contract_type_detected}`
        }
      });
      toast.success('Análisis guardado en el historial');
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Error al guardar el análisis');
    } finally {
      setSaving(false);
    }
  };

  const getRiskGradient = (score) => {
    if (score >= 70) return 'from-red-500 to-red-600';
    if (score >= 40) return 'from-orange-500 to-orange-600';
    if (score >= 20) return 'from-yellow-500 to-yellow-600';
    return 'from-green-500 to-green-600';
  };

  return (
    <div className="min-h-screen mesh-gradient">
      {/* Header */}
      <header className="glass border-b border-border sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link 
              to="/dashboard" 
              className="flex items-center space-x-3 text-slate-400 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="hidden sm:inline">Dashboard</span>
            </Link>
            <div className="flex items-center space-x-3">
              <Search className="w-6 h-6 text-emerald-500" />
              <span className="font-semibold font-['Outfit']">Analizador de Contratos</span>
            </div>
            <div className="flex items-center gap-2">
              <Link
                to="/historial-analisis"
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-400 hover:text-white transition-colors"
              >
                <Clock className="w-4 h-4" />
                <span className="hidden sm:inline">Historial</span>
              </Link>
              <Link
                to="/comparador-clausulas"
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-400 hover:text-white transition-colors"
              >
                <ArrowLeftRight className="w-4 h-4" />
                <span className="hidden sm:inline">Comparar</span>
              </Link>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {!analysis ? (
          /* Input Section */
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold font-['Outfit'] mb-3">
                Analiza tu Contrato
              </h1>
              <p className="text-slate-400 max-w-xl mx-auto">
                Sube o pega un contrato externo y nuestro asistente legal lo revisará 
                para sugerirte mejoras en cláusulas, redacción, estructura y certeza jurídica.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Main Input */}
              <div className="lg:col-span-2 space-y-4">
                {/* Input Method Tabs */}
                <div className="flex gap-2">
                  <button
                    onClick={() => setInputMethod('paste')}
                    className={`flex-1 py-3 px-4 rounded-lg font-medium flex items-center justify-center gap-2 transition-all ${
                      inputMethod === 'paste'
                        ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                        : 'bg-slate-800/50 text-slate-400 border border-slate-700 hover:bg-slate-700'
                    }`}
                  >
                    <Clipboard className="w-5 h-5" />
                    Pegar Texto
                  </button>
                  <button
                    onClick={() => setInputMethod('upload')}
                    className={`flex-1 py-3 px-4 rounded-lg font-medium flex items-center justify-center gap-2 transition-all ${
                      inputMethod === 'upload'
                        ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                        : 'bg-slate-800/50 text-slate-400 border border-slate-700 hover:bg-slate-700'
                    }`}
                  >
                    <FileUp className="w-5 h-5" />
                    Subir Archivo
                  </button>
                </div>

                {inputMethod === 'paste' ? (
                  <div className="relative">
                    <textarea
                      value={contractContent}
                      onChange={(e) => setContractContent(e.target.value)}
                      placeholder="Pega aquí el contenido del contrato que deseas analizar...

Puedes copiar el texto desde un PDF, Word o cualquier documento.

El análisis identificará:
• Cláusulas potencialmente problemáticas
• Secciones faltantes
• Mejoras de redacción
• Elementos de certeza jurídica"
                      className="w-full h-80 p-4 bg-slate-800/50 border border-slate-700 rounded-xl text-white placeholder-slate-500 resize-none focus:outline-none focus:border-emerald-500 font-mono text-sm"
                      data-testid="contract-content-input"
                    />
                    <div className="absolute bottom-3 right-3 flex gap-2">
                      <button
                        onClick={handlePaste}
                        className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm flex items-center gap-1.5"
                      >
                        <Clipboard className="w-4 h-4" />
                        Pegar
                      </button>
                      {contractContent && (
                        <button
                          onClick={() => setContractContent('')}
                          className="px-3 py-1.5 bg-slate-700 hover:bg-red-600/50 rounded-lg text-sm flex items-center gap-1.5"
                        >
                          <X className="w-4 h-4" />
                          Limpiar
                        </button>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-slate-700 hover:border-emerald-500/50 rounded-xl p-12 text-center transition-colors">
                    <input
                      type="file"
                      accept=".txt"
                      onChange={handleFileUpload}
                      className="hidden"
                      id="file-upload"
                    />
                    <label htmlFor="file-upload" className="cursor-pointer">
                      <Upload className="w-12 h-12 mx-auto text-slate-500 mb-4" />
                      <p className="text-lg font-medium mb-2">
                        {fileName || 'Arrastra un archivo o haz clic para seleccionar'}
                      </p>
                      <p className="text-sm text-slate-500">
                        Archivos de texto (.txt) - Para PDF o Word, copia y pega el texto
                      </p>
                    </label>
                  </div>
                )}

                <p className="text-sm text-slate-500">
                  {contractContent.length.toLocaleString()} caracteres
                </p>
              </div>

              {/* Options */}
              <div className="space-y-4">
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-5">
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <Target className="w-5 h-5 text-emerald-400" />
                    Opciones de Análisis
                  </h3>

                  {/* Contract Type */}
                  <div className="mb-4">
                    <label className="text-sm text-slate-400 block mb-2">
                      Tipo de Contrato (opcional)
                    </label>
                    <select
                      value={contractType}
                      onChange={(e) => { setContractType(e.target.value); setUserRole(''); }}
                      className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-emerald-500"
                      data-testid="contract-type-select"
                    >
                      <option value="">Detectar automáticamente</option>
                      {CONTRACT_TYPES.map(ct => (
                        <option key={ct.id} value={ct.id}>{ct.label}</option>
                      ))}
                    </select>
                  </div>

                  {/* User Role */}
                  {contractType && USER_ROLES[contractType] && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      className="mb-4"
                    >
                      <label className="text-sm text-slate-400 block mb-2">
                        Tu Rol en el Contrato
                      </label>
                      <select
                        value={userRole}
                        onChange={(e) => setUserRole(e.target.value)}
                        className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-emerald-500"
                        data-testid="user-role-select"
                      >
                        <option value="">Seleccionar rol</option>
                        {USER_ROLES[contractType].map(role => (
                          <option key={role.id} value={role.id}>{role.label}</option>
                        ))}
                      </select>
                    </motion.div>
                  )}

                  {/* Analyze Button */}
                  <button
                    onClick={analyzeContract}
                    disabled={analyzing || !contractContent.trim()}
                    className="w-full py-3 bg-gradient-to-r from-emerald-600 to-emerald-500 text-white rounded-xl font-semibold hover:from-emerald-700 hover:to-emerald-600 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid="analyze-contract-btn"
                  >
                    {analyzing ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Analizando...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-5 h-5" />
                        Analizar Contrato
                      </>
                    )}
                  </button>
                </div>

                {/* What We Analyze */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-5">
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <BookOpen className="w-5 h-5 text-blue-400" />
                    ¿Qué Analizamos?
                  </h3>
                  <ul className="space-y-2 text-sm text-slate-400">
                    <li className="flex items-start gap-2">
                      <FileText className="w-4 h-4 mt-0.5 text-purple-400" />
                      <span>Cláusulas problemáticas o abusivas</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Layers className="w-4 h-4 mt-0.5 text-blue-400" />
                      <span>Estructura y secciones faltantes</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <PenTool className="w-4 h-4 mt-0.5 text-cyan-400" />
                      <span>Redacción y estilo jurídico</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Scale className="w-4 h-4 mt-0.5 text-amber-400" />
                      <span>Certeza jurídica y cumplimiento legal</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </motion.div>
        ) : (
          /* Results Section */
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            {/* Back Button */}
            <div className="flex items-center justify-between mb-6">
              <button
                onClick={resetAnalysis}
                className="flex items-center gap-2 text-slate-400 hover:text-white"
              >
                <ArrowLeft className="w-4 h-4" />
                Analizar otro contrato
              </button>
              <button
                onClick={saveAnalysis}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium disabled:opacity-50"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Guardar en Historial
              </button>
            </div>

            {/* Assessment Card */}
            <div className={`bg-gradient-to-r ${getRiskGradient(analysis.assessment.risk_score)} rounded-xl p-6 mb-6`}>
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="text-center sm:text-left">
                  <h2 className="text-2xl font-bold text-white mb-1">
                    {analysis.assessment.assessment}
                  </h2>
                  <p className="text-white/80">{analysis.assessment.recommendation}</p>
                </div>
                <div className="text-center">
                  <div className="text-5xl font-bold text-white">
                    {analysis.assessment.risk_score}
                  </div>
                  <div className="text-white/80 text-sm">Puntuación de Riesgo</div>
                </div>
              </div>
            </div>

            {/* Summary Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
              {Object.entries(PRIORITY_CONFIG).map(([key, config]) => {
                const count = analysis.assessment.summary[key] || 0;
                const Icon = config.icon;
                return (
                  <div
                    key={key}
                    className={`p-4 rounded-xl border ${config.bgClass} ${config.borderClass}`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Icon className={`w-4 h-4 ${config.textClass}`} />
                      <span className={`text-sm ${config.textClass}`}>{config.label}</span>
                    </div>
                    <div className="text-2xl font-bold text-white">{count}</div>
                  </div>
                );
              })}
            </div>

            {/* Contract Info */}
            <div className="flex flex-wrap gap-3 mb-6">
              <span className="px-3 py-1.5 bg-slate-800 rounded-lg text-sm flex items-center gap-2">
                <FileText className="w-4 h-4 text-slate-400" />
                Tipo: <strong>{analysis.contract_type_detected}</strong>
              </span>
              {analysis.user_role && (
                <span className="px-3 py-1.5 bg-slate-800 rounded-lg text-sm flex items-center gap-2">
                  Tu Rol: <strong>{analysis.user_role}</strong>
                </span>
              )}
              <span className="px-3 py-1.5 bg-slate-800 rounded-lg text-sm">
                Total sugerencias: <strong>{analysis.assessment.summary.total_suggestions}</strong>
              </span>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-3 mb-6">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-slate-400" />
                <span className="text-sm text-slate-400">Filtrar:</span>
              </div>
              <select
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e.target.value)}
                className="px-3 py-1.5 bg-slate-800 border border-slate-700 rounded-lg text-sm focus:outline-none focus:border-emerald-500"
              >
                <option value="all">Todas las prioridades</option>
                {Object.entries(PRIORITY_CONFIG).map(([key, config]) => (
                  <option key={key} value={key}>{config.label}</option>
                ))}
              </select>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="px-3 py-1.5 bg-slate-800 border border-slate-700 rounded-lg text-sm focus:outline-none focus:border-emerald-500"
              >
                <option value="all">Todas las categorías</option>
                {Object.entries(CATEGORY_CONFIG).map(([key, config]) => (
                  <option key={key} value={key}>{config.label}</option>
                ))}
              </select>
            </div>

            {/* Suggestions List */}
            <div className="space-y-3">
              {filteredSuggestions.length === 0 ? (
                <div className="text-center py-12 text-slate-400">
                  <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-400" />
                  <p>No hay sugerencias que coincidan con los filtros seleccionados</p>
                </div>
              ) : (
                filteredSuggestions.map((suggestion, index) => {
                  const priorityConfig = PRIORITY_CONFIG[suggestion.priority] || PRIORITY_CONFIG.media;
                  const categoryConfig = CATEGORY_CONFIG[suggestion.category] || CATEGORY_CONFIG.clausula;
                  const typeConfig = TYPE_CONFIG[suggestion.type] || TYPE_CONFIG.advertencia;
                  const isExpanded = expandedSuggestion === index;
                  const PriorityIcon = priorityConfig.icon;
                  const CategoryIcon = categoryConfig.icon;

                  return (
                    <div
                      key={index}
                      className={`bg-slate-800/50 rounded-xl border ${priorityConfig.borderClass} overflow-hidden`}
                    >
                      <div
                        className="p-4 cursor-pointer"
                        onClick={() => setExpandedSuggestion(isExpanded ? null : index)}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${priorityConfig.bgClass}`}>
                            <PriorityIcon className={`w-4 h-4 ${priorityConfig.textClass}`} />
                          </div>
                          <div className="flex-1">
                            <div className="flex flex-wrap items-center gap-2 mb-1">
                              <span className={`text-xs px-2 py-0.5 rounded ${priorityConfig.bgClass} ${priorityConfig.textClass}`}>
                                {priorityConfig.label}
                              </span>
                              <span className={`text-xs px-2 py-0.5 rounded bg-${categoryConfig.color}-500/20 text-${categoryConfig.color}-400`}>
                                <CategoryIcon className="w-3 h-3 inline mr-1" />
                                {categoryConfig.label}
                              </span>
                              <span className={`text-xs px-2 py-0.5 rounded bg-${typeConfig.color}-500/20 text-${typeConfig.color}-400`}>
                                {typeConfig.label}
                              </span>
                            </div>
                            <h4 className="font-semibold">{suggestion.title}</h4>
                            <p className="text-sm text-slate-400 mt-1">{suggestion.description}</p>
                          </div>
                          <ChevronDown className={`w-5 h-5 text-slate-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                        </div>
                      </div>

                      <AnimatePresence>
                        {isExpanded && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="border-t border-slate-700"
                          >
                            <div className="p-4 space-y-4 bg-slate-900/50">
                              {suggestion.original_text && (
                                <div>
                                  <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm font-medium text-red-400">Texto Original:</span>
                                    <button
                                      onClick={() => copyToClipboard(suggestion.original_text)}
                                      className="text-xs text-slate-400 hover:text-white flex items-center gap-1"
                                    >
                                      <Copy className="w-3 h-3" /> Copiar
                                    </button>
                                  </div>
                                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-sm text-red-200 font-mono">
                                    {suggestion.original_text}
                                  </div>
                                </div>
                              )}

                              {suggestion.suggested_text && (
                                <div>
                                  <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm font-medium text-green-400">Sugerencia:</span>
                                    <button
                                      onClick={() => copyToClipboard(suggestion.suggested_text)}
                                      className="text-xs text-slate-400 hover:text-white flex items-center gap-1"
                                    >
                                      <Copy className="w-3 h-3" /> Copiar
                                    </button>
                                  </div>
                                  <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 text-sm text-green-200">
                                    {suggestion.suggested_text}
                                  </div>
                                </div>
                              )}

                              {suggestion.legal_basis && (
                                <div className="flex items-start gap-2 pt-2 border-t border-slate-700">
                                  <Scale className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                                  <p className="text-sm text-amber-200/80">
                                    <strong>Base Legal:</strong> {suggestion.legal_basis}
                                  </p>
                                </div>
                              )}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                })
              )}
            </div>

            {/* Tips Section */}
            {analysis.tips && analysis.tips.length > 0 && (
              <div className="mt-8 bg-blue-500/10 border border-blue-500/30 rounded-xl p-6">
                <h3 className="font-semibold mb-4 flex items-center gap-2 text-blue-300">
                  <Lightbulb className="w-5 h-5" />
                  Consejos para {analysis.contract_type_detected}
                </h3>
                <ul className="space-y-2">
                  {analysis.tips.map((tip, i) => (
                    <li key={i} className="flex items-start gap-2 text-blue-200/80 text-sm">
                      <CheckCircle className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                      {tip}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </motion.div>
        )}
      </main>
    </div>
  );
};

export default ContractAnalysisPage;
